/**
 * Global player defaults — the server side of "save as default for any user".
 *
 * Talks to the backend's /api/player/defaults (same-origin when the SPA is
 * served by the FastAPI/App Runner container). On a backend-less static host
 * the request resolves to index.html, which we detect and treat as "no backend"
 * so the player silently falls back to the device's local settings.
 */
import { getApiBase } from '../api/apiBase';

export interface PlayerDefaults { noteSpeed: number; noteSize: number; trackTilt: number; camHeight: number; camDist: number; }

const URL_ = `${getApiBase()}/api/player/defaults`;

function valid(d: any): d is PlayerDefaults {
  return d && typeof d.noteSpeed === 'number' && typeof d.noteSize === 'number' && typeof d.trackTilt === 'number';
}
const num = (v: any, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);

/** Fetch the global defaults. Returns null if there's no reachable backend. */
export async function fetchGlobalDefaults(): Promise<PlayerDefaults | null> {
  try {
    const r = await fetch(URL_, { headers: { Accept: 'application/json' } });
    if (!r.ok) return null;
    // A static SPA host answers /api/* with index.html — guard against that.
    if (!(r.headers.get('content-type') || '').includes('application/json')) return null;
    const d = await r.json();
    return valid(d)
      ? { noteSpeed: d.noteSpeed, noteSize: d.noteSize, trackTilt: d.trackTilt, camHeight: num((d as any).camHeight, 0), camDist: num((d as any).camDist, 0) }
      : null;
  } catch {
    return null;
  }
}

/** Persist the global defaults (admin). Returns true on success. */
export async function saveGlobalDefaults(d: PlayerDefaults): Promise<boolean> {
  try {
    const r = await fetch(URL_, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(d),
    });
    if (!r.ok) return false;
    if (!(r.headers.get('content-type') || '').includes('application/json')) return false;
    const j = await r.json().catch(() => null);
    return !!(j && j.ok);
  } catch {
    return false;
  }
}
